// Pure helpers for guest telemetry. None of them reads the page: callers pass the values in.

const IN_APP = /FBAN|FBAV|Instagram|Line\/|GSA\/|; wv\)/;
const SAMSUNG = /SamsungBrowser\//;
const EDGE = /Edg(?:A|iOS)?\//;
const FIREFOX = /Firefox\/|FxiOS\//;
const CHROME = /Chrome\/|CriOS\//;
const SAFARI = /Safari\//;

export function browserFamily(userAgent = "") {
  const ua = String(userAgent ?? "");
  if (IN_APP.test(ua)) return "in_app";
  if (SAMSUNG.test(ua)) return "samsung";
  if (EDGE.test(ua)) return "edge";
  if (FIREFOX.test(ua)) return "firefox";
  if (CHROME.test(ua)) return "chrome";
  if (SAFARI.test(ua)) return "safari";
  return "other";
}

export function osFamily(userAgent = "", maxTouchPoints = 0) {
  const ua = String(userAgent ?? "");
  if (/iPhone|iPod/.test(ua)) return "ios";
  if (/iPad/.test(ua)) return "ipados";
  // Since iPadOS 13 a touch iPad reports a desktop Mac user agent.
  if (/Macintosh|Mac OS X/.test(ua)) return maxTouchPoints > 1 ? "ipados" : "macos";
  if (/Android/.test(ua)) return "android";
  if (/CrOS/.test(ua)) return "chromeos";
  if (/Windows/.test(ua)) return "windows";
  if (/Linux|X11/.test(ua)) return "linux";
  return "other";
}

export function viewportBucket(width) {
  if (width < 360) return "xs";
  if (width < 480) return "s";
  if (width < 768) return "m";
  if (width < 1200) return "l";
  return "xl";
}

export function latencyBucket(milliseconds) {
  if (!(milliseconds >= 1000)) return "under_1s";
  if (milliseconds < 3000) return "1s_3s";
  if (milliseconds < 10000) return "3s_10s";
  return "10s_plus";
}

const NAVIGATION_TYPES = ["navigate", "reload", "back_forward"];

export function navType(entry) {
  const type = entry?.type;
  return NAVIGATION_TYPES.includes(type) ? type : "other";
}

const ROUTES = ["invite", "feed", "entry", "media"];

export function routeFamily(view) {
  return ROUTES.includes(view) ? view : "other";
}

export function entryCountBucket(count) {
  if (count < 1) return "0";
  if (count <= 5) return "1_5";
  if (count <= 25) return "6_25";
  return "26_plus";
}

const ERROR_CLASSES = ["Error", "TypeError", "ReferenceError", "SyntaxError", "RangeError"];

// Only the standard class name leaves the page. A message never does.
export function errorClass(error) {
  try {
    const name = error?.name;
    return ERROR_CLASSES.includes(name) ? name : "other";
  } catch {
    return "other";
  }
}

const MEDIA_ERROR_CLASSES = new Map([
  [1, "aborted"],
  [2, "network"],
  [3, "decode"],
  [4, "src_not_supported"],
]);

export function mediaErrorClass(code) {
  return MEDIA_ERROR_CLASSES.get(code) ?? "unknown";
}

const SESSION_REASONS = new Map([
  ["web-guest/session-expired", "expired"],
  // A revoked session answers session-unavailable with status 400, not 401 or 403.
  ["web-guest/session-unavailable", "revoked"],
  ["web-guest/revoked", "revoked"],
  ["web-guest/session-revoked", "revoked"],
  ["web-guest/session-invalid", "invalid"],
  ["web-guest/session-required", "missing"],
]);

/** The session-ended reason for a failed request, or null when the failure did not end the session. */
export function sessionEndReason(message, status) {
  if (SESSION_REASONS.has(message)) return SESSION_REASONS.get(message);
  if (status === 401 || status === 403) return "other";
  return null;
}

/** 16 lowercase hex characters: the first 8 bytes of SHA-256. */
export async function textFingerprint(text) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest, 0, 8), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

// Chrome-style and Firefox/Safari-style frames.
const FRAME_PATTERNS = [
  /^\s*at\s+(?:(?<fn>[A-Za-z0-9_$.<>]{1,80})\s+\()?(?<loc>.+?):(?<line>\d+):(?<col>\d+)\)?\s*$/,
  /^(?<fn>[A-Za-z0-9_$.<>]{0,80})@(?<loc>.+?):(?<line>\d+):(?<col>\d+)\s*$/,
];
const SCRIPT_FILE = /^[\w.-]+\.m?js$/;

/**
 * Keeps each frame's function token and script:line:col, with the file name
 * reduced to its basename, and drops every URL, query, hash and message.
 */
export async function stackFingerprint(stack) {
  const frames = [];
  for (const text of String(stack ?? "").split("\n")) {
    const match = FRAME_PATTERNS.map((pattern) => pattern.exec(text)).find(Boolean);
    if (!match) continue;
    const { fn, loc, line, col } = match.groups;
    const file = loc.split(/[?#]/)[0].split("/").pop();
    frames.push(`${fn || "-"} ${SCRIPT_FILE.test(file) ? file : "?"}:${line}:${col}`);
  }
  return textFingerprint(frames.length > 0 ? frames.join("\n") : "no-stack");
}
